import { defineStore } from "pinia";

import type { Rule } from "@/Connect/types";

import { useConnectStore, type EditableGroupOption } from "@/Connect/store";

interface State {
    selectedLanguages: string[];
    selectedSizes: string[];
    selectedEditableIdsByGroup: Record<string, string[]>;

    selectedVariations: number;
}

export function totalSelectedInGroup(editableGroup: EditableGroupOption) {
    return editableGroup.editableOptions.filter(option => option.value).length;
}

export const useConnectFiltersStore = defineStore("connectFilters", {
    state: (): State => ({
        selectedLanguages: [],
        selectedSizes: [],
        selectedEditableIdsByGroup: {},

        selectedVariations: 0
    }),

    getters: {
        allFiltersAreActive(state) {
            const connectStore = useConnectStore();

            if (!connectStore.hasFilterOptionsForEachType) {
                return false;
            }

            const allGroupsHaveValues =
                state.selectedEditableIdsByGroup &&
                Object.values(state.selectedEditableIdsByGroup).every(groupIds => groupIds.length);

            return !!(state.selectedLanguages.length && state.selectedSizes.length && allGroupsHaveValues);
        },

        allSelectedGroupIds(state) {
            return Object.values(state.selectedEditableIdsByGroup).reduce((acc, groupIds) => {
                acc.push(...groupIds);

                return acc;
            }, [] as string[]);
        },

        selectedFiltersForLibraryQuery(state) {
            const groupValueFilters = Object.entries(state.selectedEditableIdsByGroup).reduce(
                (acc, [groupName, valueIds]) => {
                    if (valueIds.length) {
                        acc.push({
                            name: groupName,
                            valueIds
                        });
                    }

                    return acc;
                },
                [] as { name: string; valueIds: string[] }[]
            );

            return {
                languageFilters: state.selectedLanguages,
                sizeFilters: state.selectedSizes,
                groupValueFilters
            };
        }
    },

    actions: {
        initGroupFiltersFromGroupOptions() {
            const connectStore = useConnectStore();

            this.selectedEditableIdsByGroup = Object.values(connectStore.editableGroupOptions).reduce(
                (acc, group) => {
                    acc[group.name] = [];

                    return acc;
                },
                {} as Record<string, string[]>
            );
        },

        setSelectedGroupIdsByGroup(groupName: string, valueIds: string[]) {
            this.selectedEditableIdsByGroup[groupName] = valueIds;
        },

        setFiltersFromRule(filters: Rule["variantsFilters"]) {
            if (!filters) {
                return;
            }

            this.selectedLanguages = filters.languages;
            this.selectedSizes = filters.masterTemplateIds;

            const connectStore = useConnectStore();

            this.selectedEditableIdsByGroup = Object.values(connectStore.editableGroupOptions).reduce(
                (acc, group) => {
                    acc[group.name] = group.editableOptions.reduce((groupAcc, option) => {
                        if (filters.editableGroupValueIds.includes(option.value)) {
                            groupAcc.push(option.value);
                        }

                        return groupAcc;
                    }, [] as string[]);

                    return acc;
                },
                {} as Record<string, string[]>
            );
        },

        selectAllFilters(value: boolean) {
            if (value) {
                const connectStore = useConnectStore();

                this.selectedLanguages = connectStore.languageOptions.map(option => option.value);
                this.selectedSizes = connectStore.sizeOptions.map(option => option.value);
                this.selectedEditableIdsByGroup = Object.values(connectStore.editableGroupOptions).reduce(
                    (acc, group) => {
                        acc[group.name] = group.editableOptions.map(option => option.value);

                        return acc;
                    },
                    {} as Record<string, string[]>
                );
            } else {
                this.selectedLanguages = [];
                this.selectedSizes = [];
                this.initGroupFiltersFromGroupOptions();

                this.selectedVariations = 0;
            }
        }
    }
});
